import { FUN_ERROR_CODES } from "@decibeltrade/utils";
import z from "zod/v4";

import { xchainFailure } from "@/contexts/mutations/xchain/xchain-failure";
import { addressesEqual } from "@/utils/address";
import { Logger } from "@/utils/logger";
import { AuthNotReadyError } from "@/utils/signature-auth";

const FunPaymentErrorCodeSchema = z.enum(FUN_ERROR_CODES);
export type FunPaymentErrorCode = z.infer<typeof FunPaymentErrorCodeSchema>;

/** A rejection the server answered for; repeating the same request hits the same answer. */
export class FunPaymentError extends Error {
  readonly code: FunPaymentErrorCode | undefined;
  constructor(message: string, code: FunPaymentErrorCode | undefined, cause?: unknown) {
    super(message, { cause });
    this.name = "FunPaymentError";
    this.code = code;
  }
}

/** The session answered for another account, as when the cookie predates a wallet switch. */
export class FunAccountMismatchError extends Error {
  constructor() {
    super("Fun answered for a different account");
    this.name = "FunAccountMismatchError";
  }
}

export function assertAnsweredFor(expected: string | undefined, answered: string): void {
  if (!addressesEqual(expected, answered)) throw new FunAccountMismatchError();
}

/**
 * Wraps the server's failure envelope as a {@link FunPaymentError}; anything else passes through.
 */
export function toFunPaymentError(error: unknown): unknown {
  const failure = xchainFailure(error);
  if (!failure) return error;
  const code = FunPaymentErrorCodeSchema.safeParse(failure.code);
  return new FunPaymentError(failure.error, code.success ? code.data : undefined, error);
}

export function isFunUnavailable(error: unknown): boolean {
  return error instanceof FunPaymentError && error.code === "funUnavailable";
}

/** Fun's rejection or another account's answer: repeating the same request cannot change either. */
export function isTerminalFunError(error: unknown): boolean {
  return error instanceof FunPaymentError || error instanceof FunAccountMismatchError;
}

export function funRetry(feature: string, maxRetries: number) {
  return (failureCount: number, error: Error) => {
    if (isTerminalFunError(error)) return false;
    const shouldRetry = failureCount < maxRetries;
    // The request wrapper already warns on an auth hydration race.
    if (!shouldRetry && !(error instanceof AuthNotReadyError)) Logger.error(error, { feature });
    return shouldRetry;
  };
}
